import * as React from 'react'
import {
  useForkRef,
  useEventCallback,
  useEnhancedEffect,
} from '@applift/factor'

export interface AutoSizerSize {
  height: number
  width: number
}

// Credit to https://github.com/bvaughn/react-virtualized/blob/master/source/AutoSizer/AutoSizer.js
// for the sources.

export interface AutoSizerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onResize'> {
  /**
   * Function responsible for rendering children.
   * @param {AutoSizerSize} size The grid's size.
   * @returns {React.ReactNode} The children to render.
   */
  children: (size: AutoSizerSize) => React.ReactNode
  /**
   * Default height to use for initial render; useful for SSR.
   * @default null
   */
  defaultHeight?: number
  /**
   * Default width to use for initial render; useful for SSR.
   * @default null
   */
  defaultWidth?: number
  /**
   * If `true`, disable dynamic :height property.
   * @default false
   */
  disableHeight?: boolean
  /**
   * If `true`, disable dynamic :width property.
   * @default false
   */
  disableWidth?: boolean
  /**
   * Callback to be invoked on-resize.
   * @param {AutoSizerSize} size The grid's size.
   */
  onResize?: (size: AutoSizerSize) => void
}

export const GridAutoSizer = React.forwardRef<HTMLDivElement, AutoSizerProps>(
  function AutoSizer(props, ref) {
    const {
      children,
      defaultHeight = null,
      defaultWidth = null,
      disableHeight = false,
      disableWidth = false,
      nonce,
      onResize,
      style,
      ...other
    } = props

    const [state, setState] = React.useState<{
      height: number | null
      width: number | null
    }>({
      height: defaultHeight,
      width: defaultWidth,
    })

    const rootRef = React.useRef<HTMLDivElement | null>(null)
    const parentElement = React.useRef<HTMLElement | null>(null)

    const handleResize = useEventCallback<
      [ResizeObserverEntry[], ResizeObserver],
      void
    >(entries => {
      // Guard against AutoSizer component being removed from the DOM immediately after being added.
      // This can result in invalid style values which can result in NaN values if we don't handle them.
      // See issue #150 for more context.
      if (parentElement.current) {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            const contentBoxSize = Array.isArray(entry.contentBoxSize)
              ? entry.contentBoxSize[0]
              : entry.contentBoxSize

            const newHeight = contentBoxSize?.blockSize ?? 0
            const newWidth = contentBoxSize?.inlineSize ?? 0
            if (
              (!disableHeight && state.height !== newHeight) ||
              (!disableWidth && state.width !== newWidth)
            ) {
              setState({
                height: newHeight,
                width: newWidth,
              })

              if (onResize) {
                onResize({ height: newHeight, width: newWidth })
              }
            }
          }
        }
      }
    })

    useEnhancedEffect(() => {
      parentElement.current = rootRef.current!.parentElement

      if (!parentElement.current) {
        return undefined
      }

      const resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(parentElement.current)
      return () => {
        if (parentElement.current) {
          return resizeObserver.unobserve(parentElement.current)
        }
        resizeObserver.disconnect()
      }
    }, [nonce, handleResize])

    // Outer div should not force width/height since that may prevent containers from shrinking.
    // Inner component should overflow and use calculated width/height.
    // See issue #68 for more information.
    const outerStyle: any = { overflow: 'visible' }
    const childParams: any = {}

    if (!disableHeight) {
      outerStyle.height = 0
      childParams.height = state.height
    }

    if (!disableWidth) {
      outerStyle.width = 0
      childParams.width = state.width
    }

    const handleRef = useForkRef(rootRef, ref)
    return (
      <div
        ref={handleRef}
        style={{
          ...outerStyle,
          ...style,
        }}
        {...other}
      >
        {state.height === null && state.width === null
          ? null
          : children(childParams)}
      </div>
    )
  }
)
